import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  azureApi,
  type SpCredentials,
  type DiscoveredSubscription,
  type DiscoveredResourceGroup,
  type DiscoveredKeyVault,
} from "@/api/azure";
import {
  Cloud,
  MapPin,
  Server,
  CheckCircle,
  XCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  FolderOpen,
  KeyRound,
  Globe,
  Plus,
  Settings,
  RefreshCw,
} from "lucide-react";

type WizardStep = 1 | 2 | 3 | 4 | 5 | 6;

const STEP_LABELS: Record<WizardStep, string> = {
  1: "Connection",
  2: "Subscription",
  3: "Resource Group",
  4: "Key Vault",
  5: "Region",
  6: "Review",
};

const REGIONS = [
  { id: "uksouth", name: "UK South", location: "London" },
  { id: "ukwest", name: "UK West", location: "Wales" },
  { id: "westeurope", name: "West Europe", location: "Netherlands" },
  { id: "northeurope", name: "North Europe", location: "Ireland" },
  { id: "eastus", name: "East US", location: "Virginia" },
];

export function AzureSettings() {
  const queryClient = useQueryClient();
  const [wizardMode, setWizardMode] = useState(false);

  const { data: setupData, isLoading: setupLoading } = useQuery({
    queryKey: ["azure", "setup-config"],
    queryFn: azureApi.getSetupConfig,
  });

  const isConfigured = setupData?.configured === true;

  // Auto-enter wizard if not configured
  useEffect(() => {
    if (!setupLoading && !isConfigured) {
      setWizardMode(true);
    }
  }, [setupLoading, isConfigured]);

  if (setupLoading) {
    return (
      <CenterPanel title="Azure Settings">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
          <span className="ml-2 text-sm text-text-muted">Loading configuration...</span>
        </div>
      </CenterPanel>
    );
  }

  if (wizardMode || !isConfigured || !setupData?.config) {
    return (
      <SetupWizard
        onComplete={() => {
          setWizardMode(false);
          queryClient.invalidateQueries({ queryKey: ["azure", "setup-config"] });
        }}
        onCancel={isConfigured && setupData?.config ? () => setWizardMode(false) : undefined}
      />
    );
  }

  return (
    <ConfiguredView
      config={setupData.config}
      onReconfigure={() => setWizardMode(true)}
    />
  );
}

// ---------------------------------------------------------------------------
// Configured view
// ---------------------------------------------------------------------------

function ConfiguredView({
  config,
  onReconfigure,
}: {
  config: { subscriptionId: string; tenantId: string; resourceGroup: string; keyVaultUrl: string; defaultRegion: string };
  onReconfigure: () => void;
}) {
  const { data: regionsData } = useQuery({
    queryKey: ["azure", "regions"],
    queryFn: azureApi.listRegions,
  });
  const { data: vmSizesData } = useQuery({
    queryKey: ["azure", "vm-sizes"],
    queryFn: azureApi.listVmSizes,
  });

  const regions = regionsData?.regions ?? [];
  const vmSizes = vmSizesData?.vmSizes ?? [];
  const regionName = REGIONS.find((r) => r.id === config.defaultRegion)?.name ?? config.defaultRegion;

  return (
    <CenterPanel title="Azure Settings">
      {/* Connection & Config Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Cloud size={18} />
              Platform Azure Configuration
            </span>
            <Button variant="outline" size="sm" onClick={onReconfigure}>
              <RefreshCw size={14} className="mr-1.5" />
              Reconfigure
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6 mb-4">
            <StatusItem label="Credentials" ok={true} />
            <StatusItem label="Subscription" ok={true} />
            <StatusItem label="Resource Group" ok={true} />
            <StatusItem label="Key Vault" ok={true} />
          </div>
          <div className="rounded-lg border border-border divide-y divide-border">
            <ConfigRow label="Tenant ID" value={config.tenantId} mono />
            <ConfigRow label="Subscription" value={config.subscriptionId} mono />
            <ConfigRow label="Resource Group" value={config.resourceGroup} />
            <ConfigRow label="Key Vault" value={config.keyVaultUrl} mono />
            <ConfigRow label="Default Region" value={regionName} />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Regions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin size={18} />
              Available Regions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-1.5">
              {regions.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-surface-hover">
                  <span className="text-sm text-text-primary">{r.name}</span>
                  <span className="text-xs text-text-muted">{r.location}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* VM Sizes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Server size={18} />
              VM Sizes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-1.5">
              {vmSizes.map((vm) => (
                <div key={vm.id} className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-surface-hover">
                  <div>
                    <span className="text-sm text-text-primary">{vm.name}</span>
                    <p className="text-xs text-text-muted">{vm.description}</p>
                  </div>
                  <Badge variant="outline">~${vm.costPerMonth}/mo</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </CenterPanel>
  );
}

// ---------------------------------------------------------------------------
// Setup Wizard
// ---------------------------------------------------------------------------

function SetupWizard({
  onComplete,
  onCancel,
}: {
  onComplete: () => void;
  onCancel?: () => void;
}) {
  const [step, setStep] = useState<WizardStep>(1);

  // SP credentials — empty means use server-side env vars (platform Entra ID app)
  const [spCreds, setSpCreds] = useState<SpCredentials>({
    spTenantId: "",
    spClientId: "",
    spClientSecret: "",
  });
  const [showManualCreds, setShowManualCreds] = useState(false);

  // Connection test state
  const [connectionOk, setConnectionOk] = useState(false);
  const [connectionTenantId, setConnectionTenantId] = useState("");
  const [connectionError, setConnectionError] = useState("");
  const [connectionLoading, setConnectionLoading] = useState(true);

  // Selection state
  const [selectedSubscription, setSelectedSubscription] = useState<DiscoveredSubscription | null>(null);
  const [selectedResourceGroup, setSelectedResourceGroup] = useState<DiscoveredResourceGroup | null>(null);
  const [selectedKeyVault, setSelectedKeyVault] = useState<DiscoveredKeyVault | null>(null);
  const [selectedRegion, setSelectedRegion] = useState("");

  // Create new RG form
  const [showCreateRg, setShowCreateRg] = useState(false);
  const [newRgName, setNewRgName] = useState("");
  const [newRgLocation, setNewRgLocation] = useState("uksouth");

  // Create new KV form
  const [showCreateKv, setShowCreateKv] = useState(false);
  const [newKvName, setNewKvName] = useState("");

  // Saving state
  const [saveError, setSaveError] = useState("");

  // Auto-test connection on mount using server-side credentials (platform Entra app)
  useEffect(() => {
    let cancelled = false;
    setConnectionLoading(true);
    azureApi
      .discoverConnection()
      .then((result) => {
        if (cancelled) return;
        if (result.success) {
          setConnectionOk(true);
          setConnectionTenantId(result.tenantId ?? "");
          // Auto-advance to step 2 since credentials work
          setStep(2);
        } else {
          setConnectionError(result.error ?? "Failed to connect");
          setShowManualCreds(true);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setConnectionError(err instanceof Error ? err.message : "Connection failed");
        setShowManualCreds(true);
      })
      .finally(() => {
        if (!cancelled) setConnectionLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Manual test with user-entered SP credentials
  const handleTestConnection = () => {
    setConnectionLoading(true);
    setConnectionOk(false);
    setConnectionError("");
    const payload: SpCredentials | undefined =
      spCreds.spTenantId && spCreds.spClientId && spCreds.spClientSecret
        ? spCreds
        : undefined;
    azureApi
      .discoverConnection(payload)
      .then((result) => {
        if (result.success) {
          setConnectionOk(true);
          setConnectionTenantId(result.tenantId ?? "");
        } else {
          setConnectionError(result.error ?? "Failed to connect");
        }
      })
      .catch((err) => {
        setConnectionError(err instanceof Error ? err.message : "Connection failed");
      })
      .finally(() => {
        setConnectionLoading(false);
      });
  };

  // Step 2: Subscriptions
  const { data: subsData, isLoading: subsLoading } = useQuery({
    queryKey: ["azure", "discover-subscriptions", spCreds.spClientId],
    queryFn: () => azureApi.discoverSubscriptions(spCreds),
    enabled: step >= 2 && connectionOk,
  });

  // Step 3: Resource groups
  const { data: rgData, isLoading: rgLoading } = useQuery({
    queryKey: ["azure", "discover-rgs", selectedSubscription?.id, spCreds.spClientId],
    queryFn: () => azureApi.discoverResourceGroups(selectedSubscription!.id, spCreds),
    enabled: step >= 3 && !!selectedSubscription,
  });

  // Step 4: Key vaults
  const { data: kvData, isLoading: kvLoading } = useQuery({
    queryKey: ["azure", "discover-kvs", selectedSubscription?.id, selectedResourceGroup?.name, spCreds.spClientId],
    queryFn: () => azureApi.discoverKeyVaults(selectedSubscription!.id, selectedResourceGroup!.name, spCreds),
    enabled: step >= 4 && !!selectedSubscription && !!selectedResourceGroup,
  });

  // Create RG mutation
  const createRgMutation = useMutation({
    mutationFn: (data: { subscriptionId: string; name: string; location: string }) =>
      azureApi.createResourceGroup({ ...data, ...spCreds }),
    onSuccess: (result) => {
      setSelectedResourceGroup(result.resourceGroup);
      setShowCreateRg(false);
      setNewRgName("");
    },
  });

  // Create KV mutation
  const createKvMutation = useMutation({
    mutationFn: (data: { subscriptionId: string; resourceGroupName: string; name: string; location: string; tenantId: string }) =>
      azureApi.createKeyVault({ ...data, ...spCreds }),
    onSuccess: (result) => {
      setSelectedKeyVault(result.keyVault);
      setShowCreateKv(false);
      setNewKvName("");
    },
  });

  // Save config mutation
  const saveMutation = useMutation({
    mutationFn: () =>
      azureApi.saveSetupConfig({
        subscriptionId: selectedSubscription!.id,
        tenantId: spCreds.spTenantId || connectionTenantId || selectedSubscription!.tenantId,
        resourceGroup: selectedResourceGroup!.name,
        keyVaultUrl: selectedKeyVault!.vaultUri,
        defaultRegion: selectedRegion,
        clientId: spCreds.spClientId,
        clientSecret: spCreds.spClientSecret,
      }),
    onSuccess: () => onComplete(),
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : "Failed to save configuration");
    },
  });

  const canProceed = (): boolean => {
    switch (step) {
      case 1: return connectionOk;
      case 2: return !!selectedSubscription;
      case 3: return !!selectedResourceGroup;
      case 4: return !!selectedKeyVault;
      case 5: return !!selectedRegion;
      case 6: return true;
      default: return false;
    }
  };

  const handleNext = () => setStep((step + 1) as WizardStep);
  const handleBack = () => setStep((step - 1) as WizardStep);

  const displaySteps: WizardStep[] = [1, 2, 3, 4, 5, 6];

  return (
    <CenterPanel title="Azure Setup Wizard">
      {onCancel && (
        <Button variant="ghost" size="sm" className="mb-4" onClick={onCancel}>
          <ArrowLeft size={14} className="mr-1.5" />
          Back to Settings
        </Button>
      )}

      {/* Step indicator */}
      <div className="mb-6 flex items-center gap-2">
        {displaySteps.map((s) => (
          <div key={s} className="flex items-center gap-2">
            <button
              onClick={() => s < step && setStep(s)}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                s === step
                  ? "bg-accent text-white"
                  : s < step
                    ? "bg-accent/20 text-accent cursor-pointer"
                    : "bg-surface-hover text-text-muted"
              }`}
            >
              {s < step ? <Check size={14} /> : s}
            </button>
            {s < displaySteps[displaySteps.length - 1] && (
              <div className={`h-0.5 w-8 ${s < step ? "bg-accent/40" : "bg-border"}`} />
            )}
          </div>
        ))}
        <span className="ml-3 text-sm text-text-secondary">
          {STEP_LABELS[step]}
        </span>
      </div>

      {/* Step 1: Azure Connection */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Cloud size={18} />
              Azure Connection
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {connectionLoading && (
              <div className="flex items-center gap-3 py-6">
                <Loader2 className="h-5 w-5 animate-spin text-accent" />
                <span className="text-sm text-text-secondary">Connecting to Azure using platform credentials...</span>
              </div>
            )}

            {!connectionLoading && connectionOk && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-green-500" />
                  <span className="text-sm font-medium text-text-primary">Connected to Azure</span>
                </div>
                {connectionTenantId && (
                  <div className="rounded-lg bg-surface-hover p-3">
                    <p className="text-xs text-text-muted">Tenant ID</p>
                    <p className="text-sm font-mono text-text-primary">{connectionTenantId}</p>
                  </div>
                )}
              </div>
            )}
            {!connectionLoading && !connectionOk && (
              <div className="space-y-4">
                {connectionError && (
                  <div className="flex items-center gap-2">
                    <XCircle size={18} className="text-red-500" />
                    <span className="text-sm text-red-500">{connectionError}</span>
                  </div>
                )}

                <p className="text-sm text-text-secondary">
                  {showManualCreds
                    ? "Platform credentials couldn't connect to Azure. Enter a Service Principal with Contributor access to your subscription."
                    : "Enter credentials to connect to Azure."}
                </p>

                {showManualCreds && (
                  <>
                    <div className="rounded-lg bg-surface-hover p-3">
                      <p className="text-xs text-text-muted">
                        Grant the platform app Contributor access:{" "}
                        <code className="rounded bg-background px-1 py-0.5 text-[11px]">
                          az role assignment create --assignee {"{app-client-id}"} --role Contributor --scope /subscriptions/{"{sub-id}"}
                        </code>
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-text-secondary">Tenant ID</label>
                        <Input
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          value={spCreds.spTenantId}
                          onChange={(e) => setSpCreds((prev) => ({ ...prev, spTenantId: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-text-secondary">Client ID</label>
                        <Input
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          value={spCreds.spClientId}
                          onChange={(e) => setSpCreds((prev) => ({ ...prev, spClientId: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-text-secondary">Client Secret</label>
                        <Input
                          type="password"
                          placeholder="Service principal secret"
                          value={spCreds.spClientSecret}
                          onChange={(e) => setSpCreds((prev) => ({ ...prev, spClientSecret: e.target.value }))}
                        />
                      </div>
                    </div>

                    <Button
                      size="sm"
                      disabled={connectionLoading || (!spCreds.spTenantId || !spCreds.spClientId || !spCreds.spClientSecret)}
                      onClick={handleTestConnection}
                    >
                      Test Connection
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Select Subscription */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe size={18} />
              Select Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            {subsLoading && (
              <div className="flex items-center gap-3 py-4">
                <Loader2 className="h-5 w-5 animate-spin text-accent" />
                <span className="text-sm text-text-secondary">Loading subscriptions...</span>
              </div>
            )}
            {!subsLoading && (
              <div className="space-y-2">
                {(subsData?.subscriptions ?? []).map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => {
                      setSelectedSubscription(sub);
                      // Reset downstream selections
                      setSelectedResourceGroup(null);
                      setSelectedKeyVault(null);
                    }}
                    className={`flex w-full items-center justify-between rounded-lg border p-4 text-left transition-colors ${
                      selectedSubscription?.id === sub.id
                        ? "border-accent bg-accent/5"
                        : "border-border hover:border-text-muted"
                    }`}
                  >
                    <div>
                      <span className="text-sm font-medium text-text-primary">{sub.name}</span>
                      <p className="text-xs font-mono text-text-muted">{sub.id}</p>
                    </div>
                    <Badge variant={sub.state === "Enabled" ? "default" : "outline"}>
                      {sub.state}
                    </Badge>
                  </button>
                ))}
                {(subsData?.subscriptions ?? []).length === 0 && (
                  <p className="py-4 text-center text-sm text-text-muted">
                    No subscriptions found. Ensure your account has subscription access.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Resource Group */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FolderOpen size={18} />
              Resource Group
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Create new button */}
            <Button
              variant="outline"
              size="sm"
              className="mb-4"
              onClick={() => setShowCreateRg(!showCreateRg)}
            >
              <Plus size={14} className="mr-1.5" />
              Create New
            </Button>

            {/* Create form */}
            {showCreateRg && (
              <div className="mb-4 rounded-lg border border-border p-4 space-y-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                    Resource Group Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g. rg-proclaw-production"
                    value={newRgName}
                    onChange={(e) => setNewRgName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-text-secondary">Location</label>
                  <select
                    value={newRgLocation}
                    onChange={(e) => setNewRgLocation(e.target.value)}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                  >
                    {REGIONS.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.location})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={!newRgName.trim() || createRgMutation.isPending}
                    onClick={() =>
                      createRgMutation.mutate({
                        subscriptionId: selectedSubscription!.id,
                        name: newRgName.trim(),
                        location: newRgLocation,
                      })
                    }
                  >
                    {createRgMutation.isPending ? (
                      <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Creating...</>
                    ) : (
                      "Create"
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowCreateRg(false)}>
                    Cancel
                  </Button>
                </div>
                {createRgMutation.isError && (
                  <p className="text-sm text-red-500">
                    {createRgMutation.error instanceof Error ? createRgMutation.error.message : "Failed to create resource group"}
                  </p>
                )}
              </div>
            )}

            {rgLoading && (
              <div className="flex items-center gap-3 py-4">
                <Loader2 className="h-5 w-5 animate-spin text-accent" />
                <span className="text-sm text-text-secondary">Loading resource groups...</span>
              </div>
            )}
            {!rgLoading && (
              <div className="space-y-2">
                {(rgData?.resourceGroups ?? []).map((rg) => (
                  <button
                    key={rg.name}
                    onClick={() => {
                      setSelectedResourceGroup(rg);
                      setSelectedKeyVault(null);
                    }}
                    className={`flex w-full items-center justify-between rounded-lg border p-4 text-left transition-colors ${
                      selectedResourceGroup?.name === rg.name
                        ? "border-accent bg-accent/5"
                        : "border-border hover:border-text-muted"
                    }`}
                  >
                    <div>
                      <span className="text-sm font-medium text-text-primary">{rg.name}</span>
                      <p className="text-xs text-text-muted">{rg.location}</p>
                    </div>
                    <Badge variant="outline">{rg.provisioningState}</Badge>
                  </button>
                ))}
                {(rgData?.resourceGroups ?? []).length === 0 && !showCreateRg && (
                  <p className="py-4 text-center text-sm text-text-muted">
                    No resource groups found. Create one above.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Key Vault */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound size={18} />
              Key Vault
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-xs text-text-muted">
              The Key Vault will securely store agent credentials, API keys, and tenant secrets.
            </p>

            <Button
              variant="outline"
              size="sm"
              className="mb-4"
              onClick={() => setShowCreateKv(!showCreateKv)}
            >
              <Plus size={14} className="mr-1.5" />
              Create New
            </Button>

            {showCreateKv && (
              <div className="mb-4 rounded-lg border border-border p-4 space-y-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                    Key Vault Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g. kv-proclaw-prod"
                    value={newKvName}
                    onChange={(e) => setNewKvName(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-text-muted">
                    Must be globally unique, 3-24 characters, alphanumeric and hyphens only.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={!newKvName.trim() || createKvMutation.isPending}
                    onClick={() =>
                      createKvMutation.mutate({
                        subscriptionId: selectedSubscription!.id,
                        resourceGroupName: selectedResourceGroup!.name,
                        name: newKvName.trim(),
                        location: selectedResourceGroup!.location,
                        tenantId: connectionTenantId || selectedSubscription!.tenantId,
                      })
                    }
                  >
                    {createKvMutation.isPending ? (
                      <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Creating...</>
                    ) : (
                      "Create"
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowCreateKv(false)}>
                    Cancel
                  </Button>
                </div>
                {createKvMutation.isError && (
                  <p className="text-sm text-red-500">
                    {createKvMutation.error instanceof Error ? createKvMutation.error.message : "Failed to create key vault"}
                  </p>
                )}
              </div>
            )}

            {kvLoading && (
              <div className="flex items-center gap-3 py-4">
                <Loader2 className="h-5 w-5 animate-spin text-accent" />
                <span className="text-sm text-text-secondary">Loading key vaults...</span>
              </div>
            )}
            {!kvLoading && (
              <div className="space-y-2">
                {(kvData?.keyVaults ?? []).map((kv) => (
                  <button
                    key={kv.name}
                    onClick={() => setSelectedKeyVault(kv)}
                    className={`flex w-full items-center justify-between rounded-lg border p-4 text-left transition-colors ${
                      selectedKeyVault?.name === kv.name
                        ? "border-accent bg-accent/5"
                        : "border-border hover:border-text-muted"
                    }`}
                  >
                    <div>
                      <span className="text-sm font-medium text-text-primary">{kv.name}</span>
                      <p className="text-xs font-mono text-text-muted">{kv.vaultUri}</p>
                    </div>
                    <Badge variant="outline">{kv.location}</Badge>
                  </button>
                ))}
                {(kvData?.keyVaults ?? []).length === 0 && !showCreateKv && (
                  <p className="py-4 text-center text-sm text-text-muted">
                    No key vaults found in this resource group. Create one above.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 5: Default Region */}
      {step === 5 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin size={18} />
              Default Region
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-xs text-text-muted">
              Select the default Azure region for agent deployments. This can be overridden per-tenant.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {REGIONS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedRegion(r.id)}
                  className={`flex flex-col items-start rounded-lg border p-4 text-left transition-colors ${
                    selectedRegion === r.id
                      ? "border-accent bg-accent/5"
                      : "border-border hover:border-text-muted"
                  }`}
                >
                  <span className="text-sm font-medium text-text-primary">{r.name}</span>
                  <span className="text-xs text-text-muted">{r.location}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 6: Review & Save */}
      {step === 6 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings size={18} />
              Review & Save
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-border divide-y divide-border">
              <ConfigRow label="Tenant ID" value={connectionTenantId || selectedSubscription?.tenantId || ""} mono />
              <ConfigRow label="Subscription" value={`${selectedSubscription?.name} (${selectedSubscription?.id})`} />
              <ConfigRow label="Resource Group" value={selectedResourceGroup?.name ?? ""} />
              <ConfigRow
                label="Key Vault"
                value={selectedKeyVault?.vaultUri ?? ""}
                mono
              />
              <ConfigRow
                label="Default Region"
                value={REGIONS.find((r) => r.id === selectedRegion)?.name ?? selectedRegion}
              />
            </div>

            {saveError && <p className="text-sm text-red-500">{saveError}</p>}
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="mt-6 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          disabled={step === 1}
          onClick={handleBack}
        >
          <ArrowLeft size={14} className="mr-1.5" />
          Back
        </Button>
        {step === 6 ? (
          <Button
            size="sm"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? (
              <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Saving...</>
            ) : (
              <>
                <Check size={14} className="mr-1.5" />
                Save Configuration
              </>
            )}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={!canProceed()}
            onClick={handleNext}
          >
            Next
            <ArrowRight size={14} className="ml-1.5" />
          </Button>
        )}
      </div>
    </CenterPanel>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function StatusItem({ label, ok }: { label: string; ok?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      {ok ? <CheckCircle size={14} className="text-green-500" /> : <XCircle size={14} className="text-text-muted" />}
      <span className={`text-sm ${ok ? "text-text-primary" : "text-text-muted"}`}>{label}</span>
    </div>
  );
}

function ConfigRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-xs font-medium text-text-muted">{label}</span>
      <span className={`text-sm text-text-primary ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
    </div>
  );
}
